import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { DEFAULT_APP_BRANDING } from "@/helpers/app-branding";
import { toUiLanguageId } from "@/helpers/languages";
import { canManageProjectManagement } from "@/helpers/permissions";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { useConfigurationQueries } from "@/queries/configurations/queryOptions";
import { projectMutations } from "@/queries/project/mutations";
import { useProjectQueries } from "@/queries/project/queryOptions";

import { type CreateOrEditProjectFormValues, ProjectForm, type ProjectFormProps } from "./ProjectForm";

export function CreateProjectPage(): React.ReactNode {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const hasPermission = usePermission();

  const projectQueries = useProjectQueries();
  const sessionUser = useSessionUser();
  const { data: projects = [], isPending: isLoadingProjects, error: projectsError } = useLinkedProjects();
  const {
    data: baseLanguages,
    isFetching: isFetchingBaseLanguages,
    error: baseLanguagesError,
  } = useQuery({
    ...projectQueries.languagesBase(),
    enabled: hasPermission(canManageProjectManagement),
  });
  const configurationQueries = useConfigurationQueries();
  const {
    data: appBrands,
    isPending: isPendingAppBrands,
    error: appBrandsError,
  } = useQuery(configurationQueries.listAppBrandings());

  const createProjectMutation = projectMutations.useCreateProject();

  const handleSubmit = async (formValues: CreateOrEditProjectFormValues) => {
    setIsSubmitting(true);

    const createdProjectResponse = await createProjectMutation.mutateAsync({
      city: formValues.city,
      languageId: toUiLanguageId(formValues.language.id),
      maintenance: formValues.maintenance,
      name: formValues.name,
      owner: formValues.owner,
      projectType: formValues.type,
      cloneProjectId: formValues.cloneProject?.id,
      appBranding: formValues.appBranding,
      brandedAppPrompt: formValues.appBranding !== DEFAULT_APP_BRANDING ? "mandatory" : undefined,
      developmentType: formValues.developmentType,
      allowHidePostForAdmin: formValues.allowHidePostForAdmin === "true",
    });
    void navigate(`/@/${createdProjectResponse.data.slug}`);

    setIsSubmitting(false);
  };

  const error = baseLanguagesError || projectsError || appBrandsError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isFetchingBaseLanguages || isPendingAppBrands || isLoadingProjects;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const defaultLanguage = sessionUser.language;

  const formProps: ProjectFormProps = {
    isEditMode: false,
    defaultValues: {
      language: baseLanguages?.[0] || defaultLanguage,
      type: "addressBased",
      cloneProject: projects.find((x) => x.name === "Duplicaat NieuwProject"),
      appBranding: DEFAULT_APP_BRANDING,
      activeState: "unknown",
      developmentType: "existingProperty",
      allowHidePostForAdmin: "false",
    },
    baseLanguages: baseLanguages || [],
    projects,
    projectState: "unknown",
    isSubmitting,
    closeProject: () => Promise.resolve(undefined),
    isClosingProject: false,
    onSubmit: handleSubmit,
    appBrands,
  };

  return <ProjectForm {...formProps} />;
}
